import React, { useEffect, useMemo, useState } from 'react'

import { ArrowDownNarrowWide, ArrowUpNarrowWide } from 'lucide-react'

import { cn } from '@/lib/utils'
import { useApi } from '@/hooks/useApi'
import useProjectStore from '@/store/useProjectStore'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { ButtonGroup } from '../ui/button-group'

const STATUS_COLORS = {
  default:
    'border-border/60 bg-muted/40 text-muted-foreground/90 dark:bg-muted/20',
  active:
    'border-emerald-500/40 bg-emerald-500/10 text-emerald-500 dark:text-emerald-400',
  done: 'border-sky-500/40 bg-sky-500/10 text-sky-500 dark:text-sky-400',
  blocked:
    'border-rose-500/40 bg-rose-500/10 text-rose-500 dark:text-rose-400',
  pending:
    'border-amber-500/40 bg-amber-500/10 text-amber-500 dark:text-amber-400',
}

function getStatusVariant(raw) {
  if (!raw) return STATUS_COLORS.default
  const value = String(raw).toLowerCase()
  if (value.includes('block') || value.includes('risk')) return STATUS_COLORS.blocked
  if (value.includes('done') || value.includes('complete')) return STATUS_COLORS.done
  if (value.includes('hold') || value.includes('pend')) return STATUS_COLORS.pending
  if (value.includes('active') || value.includes('progress')) return STATUS_COLORS.active
  return STATUS_COLORS.default
}

function formatDate(value) {
  if (!value) return '—'
  try {
    // Backend uses ISO date strings (YYYY-MM-DD)
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return value
  }
}

const sortFns = {
  topic: (a, b) => (a.topic || '').localeCompare(b.topic || ''),
  status: (a, b) => (a.status || '').localeCompare(b.status || ''),
  due_date: (a, b) => {
    if (!a.due_date && !b.due_date) return 0
    if (!a.due_date) return 1
    if (!b.due_date) return -1
    return new Date(a.due_date) - new Date(b.due_date)
  },
  collection: (a, b) => (a.collection || '').localeCompare(b.collection || ''),
}

const columns = [
  { id: 'topic', label: 'Project', sortable: true },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'due_date', label: 'Due date', sortable: true },
  { id: 'category', label: 'Category', sortable: false },
  { id: 'collection', label: 'Collection', sortable: true },
]

/**
 * Lightweight, read-only project grid for the ProjectPanel.
 *
 * This intentionally avoids bringing in the full DiceUI data-grid stack yet,
 * but mirrors the layout and interaction model: pinned headers, scrollable body,
 * sortable columns, and compact rows. Data is sourced from `useProjectStore`,
 * which is already populated by `Layout` via the `/projects` API.
 */
const ProjectGrid = ({ className }) => {
  const api = useApi()
  const projects = useProjectStore(state => state.projects)
  const archivedProjects = useProjectStore(state => state.archivedProjects)
  const fetchArchivedProjects = useProjectStore(
    state => state.fetchArchivedProjects,
  )
  const [view, setView] = useState('active')
  const [sortBy, setSortBy] = useState({ columnId: 'due_date', direction: 'asc' })
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!api || view !== 'archived') return
    fetchArchivedProjects(api)
  }, [api, view, fetchArchivedProjects])

  const rows = view === 'archived' ? archivedProjects : projects

  const handleSortClick = columnId => {
    setSortBy(prev => {
      if (prev.columnId !== columnId) return { columnId, direction: 'asc' }
      if (prev.direction === 'asc') return { columnId, direction: 'desc' }
      return { columnId: 'due_date', direction: 'asc' }
    })
  }

  const filteredAndSorted = useMemo(() => {
    let filteredRows = Array.isArray(rows) ? [...rows] : []

    if (search.trim() !== '') {
      const q = search.toLowerCase()
      filteredRows = filteredRows.filter(row => {
        return [row.topic, row.details, row.notes, row.category, row.collection]
          .filter(Boolean)
          .some(val => String(val).toLowerCase().includes(q))
      })
    }

    const sortFn = sortFns[sortBy.columnId]
    if (sortFn) {
      filteredRows.sort(sortFn)
      if (sortBy.direction === 'desc') filteredRows.reverse()
    }

    return filteredRows
  }, [rows, sortBy.columnId, sortBy.direction, search])

  const hasRows = filteredAndSorted.length > 0

  return (
    <div
      className={cn(
        // 'flex h-full min-h-0 flex-col rounded-xl border border-border/60 bg-card/80 shadow-inner backdrop-blur-sm',
        // New-chat is always dark; keep the table readable even in light theme.
        // 'new-chat:border-white/15 new-chat:bg-white/10 new-chat:text-white',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 rounded-t-md py-3 text-[11px] new-chat:border-transparent">
        <div className="flex items-center gap-2 text-muted-foreground new-chat:text-white/70">
          <span className="font-semibold uppercase tracking-[0.18em] text-primary new-chat:text-white/90">
            Project table
          </span>
          <span className="hidden text-[11px] text-muted-foreground/80 sm:inline new-chat:text-white/60">
            {hasRows
              ? `Showing ${filteredAndSorted.length} project${
                  filteredAndSorted.length === 1 ? '' : 's'
                }`
              : 'No projects yet'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <ButtonGroup>
            <Button
              size="sm"
              variant={view === 'active' ? 'secondary' : 'outline'}
              onClick={() => setView('active')}
            >
              Active
            </Button>
            <Button
              size="sm"
              variant={view === 'archived' ? 'secondary' : 'outline'}
              onClick={() => setView('archived')}
            >
              Archived
            </Button>
          </ButtonGroup>
          <Input
            type="search"
            name="search"
            className={cn(
              'h-7 w-32 sm:w-44 text-xs border-white/15',
            )}
            placeholder="Filter by keyword…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto overscroll-contain">
        <table className="min-w-full border-separate border-spacing-0 text-xs">
          <thead className="sticky top-0 z-20 bg-background/80"

          >

            <tr className="border-b border-border/70 text-[11px] uppercase tracking-[0.16em] text-muted-foreground new-chat:border-white/15 new-chat:text-white/70">
              {columns.map(col => {
                const isSorted = sortBy.columnId === col.id && sortBy.direction !== null
                const Icon =
                  isSorted && sortBy.direction === 'desc'
                    ? ArrowDownNarrowWide
                    : ArrowUpNarrowWide

                return (
                  <th
                    key={col.id}
                    scope="col"
                    className={cn(
                      'whitespace-nowrap px-4 py-2 text-left align-middle font-medium',
                      col.id === 'topic' && 'min-w-[12rem] sm:min-w-[14rem]',
                      col.id === 'details' && 'min-w-[16rem]',
                    )}
                  >
                    <button
                      type="button"
                      className={cn(
                        'group inline-flex items-center gap-1 rounded-full px-1.5 py-0.5',
                        col.sortable &&
                          'transition-colors hover:bg-muted/60 hover:text-foreground',
                        col.sortable &&
                          'new-chat:hover:bg-white/10 new-chat:hover:text-white',
                      )}
                      disabled={!col.sortable}
                      onClick={() => col.sortable && handleSortClick(col.id)}
                    >
                      <span>{col.label}</span>
                      {col.sortable && (
                        <span className="text-foreground/40 opacity-0 transition-opacity group-hover:opacity-100">
                          {isSorted ? <Icon className="h-3 w-3" /> : <ArrowUpNarrowWide className="h-3 w-3" />}
                        </span>
                      )}
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {hasRows ? (
              filteredAndSorted.map(row => (
                <tr
                  key={row.clientId || row.id}
                  className="border-b border-border/40 last:border-b-0 hover:bg-muted/40 new-chat:border-white/10 new-chat:hover:bg-white/5"
                >
                  <td className="max-w-[18rem] px-4 py-2 align-top text-xs new-chat:text-white/85">
                    <div className="flex flex-col gap-0.5">
                      <span className="truncate font-medium text-foreground new-chat:text-white/90" title={row.topic || '(Untitled project)'}>
                        {row.topic || <span className="text-muted-foreground/70">(Untitled project)</span>}
                      </span>
                      {row.details && (
                        <span className="line-clamp-2 text-[11px] text-muted-foreground new-chat:text-white/60" title={row.details}>
                          {row.details}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="w-32 px-4 py-2 align-top new-chat:text-white/80">
                    <span
                      className={cn(
                        'inline-flex max-w-full items-center gap-1 truncate rounded-full border px-2 py-0.5 text-[11px] font-medium',
                        'new-chat:border-white/15 new-chat:bg-white/10 new-chat:text-white/80',
                        getStatusVariant(row.status),
                      )}
                    >
                      <span className="truncate capitalize">{row.status || '—'}</span>
                    </span>
                  </td>
                  <td className="w-32 px-4 py-2 align-top text-[11px] text-muted-foreground new-chat:text-white/60">
                    {formatDate(row.due_date)}
                  </td>
                  <td className="w-32 px-4 py-2 align-top text-[11px] text-muted-foreground new-chat:text-white/60">
                    {row.category || '—'}
                  </td>
                  <td className="w-32 px-4 py-2 align-top text-[11px] text-muted-foreground new-chat:text-white/60">
                    {row.collection || '—'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-[13px] text-muted-foreground new-chat:text-white/70"
                >
                  <p className="mb-1 font-medium">No projects to show yet</p>
                  <p className="text-[12px] text-muted-foreground/90 new-chat:text-white/60">
                    Ask the assistant to create a new project plan or import existing work,
                    and they will appear here automatically.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ProjectGrid
